import {DAYS_IN_WEEK, WEEKS_IN_YEAR} from '@wandb/weave/common/util/time';
import {Button} from '@wandb/weave/components/Button';
import {capitalize} from 'lodash';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ActivityCalendar from '../../components/ActivityCalendar';
import {
  useNavContext,
  useNavUpdaterContext,
} from '../../components/NavContextProvider';
import RunsTable, {RunsTableRun} from '../../components/RunsTable';
import {
  SectionHeaderActions,
  SectionNew as Section,
} from '../../components/Section';
import {SectionTopHeader} from '../../components/Section/SectionNew';
import DeferredQueryData from '../../containers/DeferredQueryData';
import {useFetchTeamProfilePageProjectsByIDsQuery} from '../../generated/graphql';
import {useCachedUserRunsActivityQuery} from '../../graphql/profilePage';
import {Analytics} from '../../services/analytics';
import {useApolloClient} from '../../state/hooks';
import {pushReport} from '../../state/reports/hooks';
import {Graph} from '../../types/graphql';
import {useEntitySettings} from '../../util/entitySettings';
import history from '../../util/history';
import {isPublicRead} from '../../util/projectAccess';
import {useRampFlagTeamReadmeSection} from '../../util/rampFeatureFlags';
import {getEmptyReportConfig} from '../../util/report';
import {allProjects} from '../../util/urls';
import {
  CustomizeShowcasedProjectsModalTeam,
  CustomizeShowcasedProjectsModalUser,
  CustomizeShowcasedReportsModalUser,
  EntityPageSection,
  LinksSection,
  ProjectShowcaseSection,
  ReportShowcaseSection,
  TeamReadmeSection,
  useEntityPageContext,
} from '../EntityPage';
import {EmptyState} from '../EntityPage/EmptyState';
import * as EntityS from '../EntityPage/EntityPage.styles';
import CreateProjectDrawer from '../HomePage/HomePageSidebar/CreateProjectDrawer';
import {projectsFromShowcasedProjectsQueryResult} from '../ProfilePage/utils';
import {useTeamPageProjects} from './useTeamProjects';

interface EntityPageProps {
  entityName: string;
  runs: Graph<RunsTableRun> | null;
  runsIsLoading: boolean;
  analyticsLocation: string;
  isTeamPage: boolean;
  setRunNameSearchStr?: (prevState: string) => void;
}
function getDailyRunCountLimit(): number {
  // Fills activity calendar with a year of data,
  // with some adjustment for alignment reasons.
  const adjustmentForAlignment = 1;
  return (
    DAYS_IN_WEEK * (WEEKS_IN_YEAR - 1) +
    adjustmentForAlignment +
    new Date().getDay()
  );
}

export const EntityOverviewPage: FC<EntityPageProps> = React.memo(
  ({entityName, isTeamPage, setRunNameSearchStr, runs, analyticsLocation}) => {
    type ProfilePageSection = {
      profilePageCard: React.ReactNode;
      ctaButton?: React.ReactNode;
    };

    const {
      sectionOrder,
      showcasedProjectIDs,
      showcasedReportIDs,
      links,
      readOnly,
    } = useEntityPageContext();

    const showTeamReadmeSection = useRampFlagTeamReadmeSection();
    const client = useApolloClient();
    const {isCreatingProject} = useNavContext();
    const {toggleCreateProjectDrawer} = useNavUpdaterContext();
    const showcasedProjectsQueryResult =
      useFetchTeamProfilePageProjectsByIDsQuery({
        variables: {
          entityName,
          projectIDs: showcasedProjectIDs,
        },
        skip: showcasedProjectIDs.length === 0,
      });
    const showcasedProjects = useMemo(
      () =>
        projectsFromShowcasedProjectsQueryResult(showcasedProjectsQueryResult),
      [showcasedProjectsQueryResult]
    );

    useEffect(() => {
      if (showcasedProjectsQueryResult.loading) {
        return;
      }
      if (showcasedProjects.length === 0) {
        // Prevent clutter, don't track if there are no showcased projects
        return;
      }
      Analytics.track('showcased_projects_number', {
        entity: entityName,
        count: showcasedProjects.length,
        location: analyticsLocation,
      });
    }, [
      showcasedProjects,
      showcasedProjectsQueryResult,
      entityName,
      analyticsLocation,
    ]);

    const createReportForProject = useCallback(
      // `p.entityName` here is the specific project's entity name.
      // Since the project can be from a different entity, `p.entityName`
      // may differ from the `entityName` in the component scope.
      (p: {name: string; entityName: string}) => {
        pushReport({
          client,
          entityName: p.entityName,
          projectName: p.name,
          config: getEmptyReportConfig(),
        });
      },
      [client]
    );
    const {entitySettings} = useEntitySettings(entityName);
    const hideActivityCalendar =
      (entitySettings?.[`hideActivityCalendar`] || isTeamPage) ?? false;
    const [editingTeamReadme, setEditingTeamReadme] = useState(false);
    const [
      customizeShowcasedProjectsModalOpen,
      setCustomizeShowcasedProjectsModalOpen,
    ] = useState(false);

    const openCustomizeShowcasedProjectsModal = useCallback(() => {
      setCustomizeShowcasedProjectsModalOpen(true);
    }, []);

    const showProjectsSectionEmptyState =
      !readOnly && showcasedProjects.length === 0;

    const closeCustomizeShowcasedProjectsModal = useCallback(() => {
      setCustomizeShowcasedProjectsModalOpen(false);
    }, []);
    const {data: teamPageProjectsData} = useTeamPageProjects(entityName);

    const projects = teamPageProjectsData?.entity.projects ?? [];
    const teamHasProjects = projects.length > 0;

    const teamHasPublicProjects = projects.some(isPublicRead);
    const teamHasPublicReports =
      teamPageProjectsData?.entity?.runs.some(r => isPublicRead(r.project)) ??
      false;

    const projectToCreateReportFor = projects.find(isPublicRead) ?? null;

    const showReportsSectionEmptyState =
      !readOnly &&
      showcasedReportIDs.length === 0 &&
      (teamHasPublicReports || projectToCreateReportFor != null);

    const [
      customizeShowcasedReportsModalOpen,
      setCustomizeShowcasedReportsModalOpen,
    ] = useState(false);

    const openCustomizeShowcasedReportsModal = useCallback(() => {
      setCustomizeShowcasedReportsModalOpen(true);
    }, []);
    const [addingLink, setAddingLink] = useState(false);
    const startAdding = useCallback(() => {
      setAddingLink(true);
    }, []);
    const closeCustomizeShowcasedReportsModal = useCallback(() => {
      setCustomizeShowcasedReportsModalOpen(false);
    }, []);

    const userRunActivityQuery = useCachedUserRunsActivityQuery(
      entityName ?? '',
      getDailyRunCountLimit()
    );

    const showLinksSectionEmptyState = !readOnly && links.length === 0;

    const reorderableContentBySectionID: {
      [id in EntityPageSection]: ProfilePageSection;
    } = {
      teamReadme: {
        profilePageCard: (
          <TeamReadmeSection
            editing={editingTeamReadme}
            onEdit={() => setEditingTeamReadme(true)}
            onCancelEdit={() => setEditingTeamReadme(false)}
          />
        ),
        ctaButton: (
          <Button variant="ghost" onClick={() => setEditingTeamReadme(true)}>
            Edit
          </Button>
        ),
      },
      projects: {
        profilePageCard: (
          <>
            {showProjectsSectionEmptyState ? (
              teamHasProjects ? (
                <EmptyState
                  iconName="report"
                  header={`Highlight your latest projects`}
                  body={`Make a project public to showcase here!`}
                  buttonText={`View my projects`}
                  onClickButton={() => history.push(allProjects(entityName))}
                />
              ) : (
                <>
                  <EmptyState
                    iconName="report"
                    header={`Highlight your latest projects`}
                    body={`Get started with Weights & Biases by creating your first project.`}
                    buttonText={`Create a project`}
                    onClickButton={() => toggleCreateProjectDrawer()}
                  />
                  <CreateProjectDrawer
                    open={isCreatingProject}
                    entityName={entityName}
                    toggleProjectDrawer={toggleCreateProjectDrawer}
                  />
                </>
              )
            ) : (
              <ProjectShowcaseSection projects={showcasedProjects} />
            )}
            {customizeShowcasedProjectsModalOpen &&
              (isTeamPage ? (
                <CustomizeShowcasedProjectsModalTeam
                  entityName={entityName}
                  onClose={closeCustomizeShowcasedProjectsModal}
                />
              ) : (
                <CustomizeShowcasedProjectsModalUser
                  username={entityName}
                  onClose={closeCustomizeShowcasedProjectsModal}
                />
              ))}
          </>
        ),
        ctaButton:
          showProjectsSectionEmptyState && teamHasPublicProjects ? (
            <Button
              variant="ghost"
              onClick={openCustomizeShowcasedProjectsModal}>
              Select a project to showcase
            </Button>
          ) : !readOnly && !showProjectsSectionEmptyState ? (
            <SectionHeaderActions>
              <EntityS.SelectShowcaseButton
                onClick={openCustomizeShowcasedProjectsModal}>
                Customize your projects
              </EntityS.SelectShowcaseButton>
            </SectionHeaderActions>
          ) : null,
      },
      activity: {
        profilePageCard: (
          <>
            <DeferredQueryData
              name="profile-page-activity-calendar"
              query={userRunActivityQuery}>
              {runsData => (
                <ActivityCalendar
                  dailyRunCount={runsData?.user?.dailyRunCount ?? []}
                  suppressHeader
                />
              )}
            </DeferredQueryData>
          </>
        ),
      },
      runs: {
        profilePageCard: (
          <>
            <RunsTable
              data={runs}
              name="Runs"
              showUserColumn={isTeamPage}
              setRunNameSearchStr={setRunNameSearchStr}
              suppressHeader
            />
          </>
        ),
      },
      reports: {
        profilePageCard: (
          <>
            {showReportsSectionEmptyState ? (
              teamHasPublicReports ? (
                <EmptyState
                  iconName="report"
                  header={`Share insights and showcase your findings`}
                  body={`Reports are your brain dumps, eureka moments, and everything in between. They play nicely with everything else in W&B, so you can share, store or explain your findings.`}
                  buttonText={`Showcase a report`}
                  onClickButton={openCustomizeShowcasedReportsModal}
                />
              ) : projectToCreateReportFor != null ? (
                <EmptyState
                  iconName="report"
                  header={`Share insights and showcase ${projectToCreateReportFor.name}`}
                  body={`Reports are your brain dumps, eureka moments, and everything in between. They play nicely with everything else in W&B, so you can share, store or explain your findings.`}
                  buttonText={`Create a report`}
                  onClickButton={() =>
                    createReportForProject(projectToCreateReportFor)
                  }
                />
              ) : null
            ) : (
              <ReportShowcaseSection />
            )}
            {customizeShowcasedReportsModalOpen && (
              <CustomizeShowcasedReportsModalUser
                onClose={closeCustomizeShowcasedReportsModal}
              />
            )}
          </>
        ),
        ctaButton:
          !readOnly && !showReportsSectionEmptyState ? (
            <Button
              variant="ghost"
              onClick={openCustomizeShowcasedReportsModal}>
              Select reports to showcase
            </Button>
          ) : null,
      },
      links: {
        profilePageCard: (
          <LinksSection
            showEmptyState={showLinksSectionEmptyState}
            setAddingLink={setAddingLink}
            addingLink={addingLink}
            startAdding={startAdding}
          />
        ),
        ctaButton: !readOnly && !showLinksSectionEmptyState && (
          <Button variant="ghost" onClick={startAdding}>
            Add a new link
          </Button>
        ),
      },
    };
    const reorderableSections = sectionOrder.map((sectionID, sectionIndex) => {
      if (sectionID === `teamReadme` && !showTeamReadmeSection) {
        return null;
      }
      if (
        sectionID === `projects` &&
        showcasedProjectIDs.length === 0 &&
        !showProjectsSectionEmptyState
      ) {
        return null;
      }
      if (
        sectionID === `reports` &&
        showcasedReportIDs.length === 0 &&
        !showReportsSectionEmptyState
      ) {
        return null;
      }
      if (
        sectionID === `links` &&
        links.length === 0 &&
        !showLinksSectionEmptyState
      ) {
        return null;
      }
      if (sectionID === `activity` && hideActivityCalendar === true) {
        return null;
      }
      return (
        <Fragment key={sectionID}>
          <SectionTopHeader>
            {sectionID === 'teamReadme' ? 'About' : capitalize(sectionID)}
            <div>{reorderableContentBySectionID[sectionID].ctaButton}</div>
          </SectionTopHeader>
          <Section key={sectionIndex} transparentBackground noPadding>
            {reorderableContentBySectionID[sectionID].profilePageCard}
          </Section>
        </Fragment>
      );
    });

    return <>{reorderableSections}</>;
  }
);
